import React from 'react'
import { Box, makeStyles, Paper, Typography } from '@material-ui/core'
import { useSearchOfficer } from '../../../../services/um/useSearchOfficer'
import SectionArray from './SectionArray'
import { PersonOption } from '../agendaTypes'
import useTranslate from '../../../../i18n/useTranslate'
import MyTextfield from '../../../../control/textfield/textfield'
import TooltipControl from '../../../../control/tooltip/tooltip'
import pdfDocumentIcon from '../../../../images/pdf_document.svg'
import docxDocumentIcon from '../../../../images/docx_document.svg'
import pdfDocumentWithPenIcon from '../../../../images/pdf_document_with_pen.svg'

type PersonInfo = {
    email?: string
    fullnameTh?: string
    fullnameEn?: string
}

const MeetingParticipantsForm: React.FC<{
    prefix?: string
    canEdit?: boolean
    isSigned?: boolean
    showLeavers?: boolean
    showAgendaSummary?: boolean
    showExportButton?: boolean
    onExportSummaryPdf?: () => void
    onExportSummaryPdfSigned?: () => void
    onExportSummaryDocx?: () => void
    onExportSummaryDocxSigned?: () => void
}> = ({
    prefix = 'participants',
    canEdit = true,
    isSigned = false,
    showLeavers = false,
    showAgendaSummary = false,
    showExportButton = false,
    onExportSummaryPdf,
    onExportSummaryPdfSigned,
    onExportSummaryDocx,
    onExportSummaryDocxSigned,
}) => {
    const classes = useStyles()
    const t = useTranslate()
    const { officers = [], searchOfficers } = useSearchOfficer(false)
    React.useEffect(() => {
        searchOfficers({ usergroupID: '5' } as any)
    }, [searchOfficers])

    const committeeOption = React.useMemo<PersonOption[]>(
        () =>
            (officers ?? [])
                .map((e: any) => ({ value: e.userID, label: e.nameTH || '—' }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [officers],
    )

    const idToEmail = React.useMemo<Record<string, string>>(
        () => Object.fromEntries((officers ?? []).map((e: any) => [String(e.userID), e.email ?? ''])),
        [officers],
    )

    const idToPerson = React.useMemo<Record<string, PersonInfo>>(
        () =>
            Object.fromEntries(
                (officers ?? []).map((e: any) => [
                    String(e.userID),
                    {
                        email: e.email ?? '',
                        fullnameTh: e.nameTH ?? e.name ?? '',
                        fullnameEn: e.nameEN ?? '',
                    } as PersonInfo,
                ]),
            ),
        [officers],
    )
    return (
        <Paper className={classes.root} elevation={0}>
            <Box className={classes.headerBar}>
                <Typography variant="h6" style={{ fontWeight: 700 }}>
                    {t.get('UM.SETTING_AGENDA.PARTICIPANTS.HEADING')}
                </Typography>
                {showExportButton && (
                    <Box className={classes.actionRow}>
                        <TooltipControl title={t.get('UM.SETTING_AGENDA.BUTTON.EXPORT_SUMMARY_PDF')} placement="bottom">
                            <div style={{ display: 'flex' }} onClick={onExportSummaryPdf}>
                                <img
                                    src={pdfDocumentIcon}
                                    alt="pdf-document-icon"
                                    style={{ height: 40, cursor: 'pointer' }}
                                />
                            </div>
                        </TooltipControl>
                        {isSigned && (
                            <>
                                <TooltipControl
                                    title={t.get('UM.SETTING_AGENDA.BUTTON.EXPORT_SUMMARY_PDF_SIGNED')}
                                    placement="bottom"
                                >
                                    <div style={{ display: 'flex' }} onClick={onExportSummaryPdfSigned}>
                                        <img
                                            src={pdfDocumentWithPenIcon}
                                            alt="pdf-document-with-pen-icon"
                                            style={{ height: 40, cursor: 'pointer' }}
                                        />
                                    </div>
                                </TooltipControl>
                            </>
                        )}
                        <TooltipControl
                            title={t.get('UM.SETTING_AGENDA.BUTTON.EXPORT_SUMMARY_DOCX')}
                            placement="bottom"
                        >
                            <div style={{ display: 'flex' }} onClick={onExportSummaryDocx}>
                                <img
                                    src={docxDocumentIcon}
                                    alt="docx-document-icon"
                                    style={{ height: 40, cursor: 'pointer' }}
                                />
                            </div>
                        </TooltipControl>
                    </Box>
                )}
            </Box>

            <SectionArray
                title={t.get('UM.SETTING_AGENDA.PARTICIPANTS.ATTENDANCES')}
                name="attendances"
                prefix={prefix}
                required
                requiredEmail
                options={committeeOption}
                idToEmail={idToEmail}
                idToPerson={idToPerson}
                canEdit={canEdit}
            />

            {showLeavers && (
                <SectionArray
                    title={t.get('UM.SETTING_AGENDA.PARTICIPANTS.ON_LEAVES')}
                    name="onLeaves"
                    prefix={prefix}
                    options={committeeOption}
                    idToEmail={idToEmail}
                    idToPerson={idToPerson}
                    canEdit={canEdit}
                />
            )}

            {/* <SectionArray
                title={t.get('UM.SETTING_AGENDA.PARTICIPANTS.EXTERNAL')}
                name="externalAttendances"
                prefix={prefix}
                isExternal
                canEdit={canEdit}
            /> */}

            <SectionArray
                title={t.get('UM.SETTING_AGENDA.PARTICIPANTS.PRESIDENTS')}
                name="presidents"
                prefix={prefix}
                required
                requiredEmail
                minItems={1}
                options={committeeOption}
                idToEmail={idToEmail}
                idToPerson={idToPerson}
                canEdit={canEdit}
            />

            <SectionArray
                title={t.get('UM.SETTING_AGENDA.PARTICIPANTS.VICE_PRESIDENTS')}
                name="vicePresidents"
                prefix={prefix}
                required
                requiredEmail
                minItems={1}
                options={committeeOption}
                idToEmail={idToEmail}
                idToPerson={idToPerson}
                canEdit={canEdit}
            />

            <SectionArray
                title={t.get('UM.SETTING_AGENDA.PARTICIPANTS.SECRETARIES')}
                name="secretaries"
                prefix={prefix}
                required
                requiredEmail
                minItems={1}
                options={committeeOption}
                idToEmail={idToEmail}
                idToPerson={idToPerson}
                canEdit={canEdit}
            />

            {showAgendaSummary && (
                <Box className={classes.section} style={{ marginBottom: 16 }}>
                    <MyTextfield
                        name="agendaSummary"
                        resetButtom
                        materialInputProps={{
                            label: t.get('UM.SETTING_AGENDA.MEETING_SUMMARY_DETAIL'),
                            placeholder: t.get('UM.SETTING_AGENDA.MEETING_SUMMARY_DETAIL'),
                            disabled: !canEdit,
                            multiline: true,
                            rows: 2,
                        }}
                    />
                </Box>
            )}
        </Paper>
    )
}

const useStyles = makeStyles((theme) => ({
    root: { borderRadius: 12, border: '1px solid #E5E7EB', background: '#fff' },
    sectionHeader: { padding: theme.spacing(3), display: 'flex', alignItems: 'center', gap: 12, fontWeight: 700 },
    section: { padding: theme.spacing(3), borderTop: '1px solid #E5E7EB' },
    headerBar: {
        padding: theme.spacing(3),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 16,
        fontWeight: 700,
    },
    actionRow: { display: 'flex', gap: 12, flexWrap: 'wrap' },
}))

export default MeetingParticipantsForm
